import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { RecursivePartial } from '@models/apollo';
import { CurrentUser } from '@models/user';
import { TranslateService } from '@ngx-translate/core';
import { BookDuringPurchaseEnum, Order, ShoppingCart } from '@queries/generated';
import { EcommerceTrackingService } from '@services/ecommerce-tracking.service';
import { ShoppingCartService } from '@services/shopping-cart.service';
import { StorefrontsService } from '@services/storefronts.service';
import { StripeService } from '@services/stripe.service';
import { SwitchFade } from '@util/animations';
import { insertIf } from '@util/insert-if';
import { IWizard } from '@util/wizard';
import { MessageService } from 'primeng/api';
import { BaseWizard } from '../base-wizard';
import { SidebarToggleComponent } from '../sidebar-toggle/sidebar-toggle.component';

enum CheckoutWizardStepsEnum {
  Cart,
  Booking,
  Billing,
  Payment,
  Receipt
}

interface CheckoutWizard extends IWizard<CheckoutWizardStepsEnum> {
  order: RecursivePartial<Order>;
}

@Component({
  selector: 'vnm-checkout-wizard',
  templateUrl: './checkout-wizard.component.html',
  styleUrls: ['./checkout-wizard.component.scss'],
  animations: [SwitchFade]
})
export class CheckoutWizardComponent extends BaseWizard implements OnChanges {
  CheckoutWizardStepsEnum = CheckoutWizardStepsEnum;

  @Input() currentUser!: CurrentUser;
  @Input() shoppingCart!: ShoppingCart;

  wizard: CheckoutWizard = {
    currentStep: CheckoutWizardStepsEnum.Cart,
    steps: [],
    running: false,
    order: null,
    errors: []
  };

  canCreateOrder$ = this.storefrontsService.canCreateOrder$;

  constructor(
    private translateService: TranslateService,
    private storefrontsService: StorefrontsService,
    private shoppingCartService: ShoppingCartService,
    private stripeService: StripeService,
    private messageService: MessageService,
    private ecom: EcommerceTrackingService,
    public sidebarToggle: SidebarToggleComponent
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.shoppingCart) {
      const { expectedTotal } = this.shoppingCart;

      this.wizard.steps = [
        {
          label: this.translateService.instant('checkout-wizard.steps.cart.title'),
          automationId: CheckoutWizardStepsEnum.Cart
        },
        ...insertIf(this.isBookable(), {
          label: this.translateService.instant('checkout-wizard.steps.booking.title'),
          automationId: CheckoutWizardStepsEnum.Booking
        }),
        {
          label: this.translateService.instant('checkout-wizard.steps.billing.title'),
          automationId: CheckoutWizardStepsEnum.Billing
        },
        // if we have $0 total skip the payment step and just create the order
        ...insertIf(expectedTotal, {
          label: this.translateService.instant('checkout-wizard.steps.payment.title'),
          automationId: CheckoutWizardStepsEnum.Payment
        }),
        {
          label: this.translateService.instant('checkout-wizard.steps.receipt.title'),
          automationId: CheckoutWizardStepsEnum.Receipt
        }
      ];
    }
  }

  override nextStep() {
    const currentStep = this.wizard.steps[this.wizard.currentStep].automationId;

    if (currentStep === CheckoutWizardStepsEnum.Billing) {
      this.ecom.beginCheckout(this.shoppingCart);

      if (!this.shoppingCart.expectedTotal) {
        this.createOrder();
      } else {
        super.nextStep();
      }
    } else {
      super.nextStep();
    }
  }

  createOrder() {
    this.stripeService.createOrder({ gatewayInfo: { token: '', intent: true } }).subscribe(
      ({ order }) => {
        this.wizard.order = order;
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('checkout.success')
        });

        super.nextStep();
      },
      ({ message }) => {
        this.messageService.add({
          severity: 'error',
          summary: message
        });
      }
    );
  }

  labelForStep(step: CheckoutWizardStepsEnum): string {
    const { expectedTotal } = this.shoppingCart;
    const currentStep = this.wizard.steps[this.wizard.currentStep].automationId;

    switch (currentStep) {
      case CheckoutWizardStepsEnum.Cart:
        const key = this.isBookable() ? 'checkout-wizard.steps.cart.button' : 'checkout-wizard.steps.booking.button';
        return this.translateService.instant(key);
      case CheckoutWizardStepsEnum.Booking:
        return this.translateService.instant('checkout-wizard.steps.booking.button');
      case CheckoutWizardStepsEnum.Billing:
        return this.translateService.instant(`checkout-wizard.steps.${expectedTotal ? 'billing' : 'payment'}.button`);
    }
  }

  showFooter(): boolean {
    const currentStep = this.wizard.steps[this.wizard.currentStep].automationId;
    const steps = [CheckoutWizardStepsEnum.Cart, CheckoutWizardStepsEnum.Booking, CheckoutWizardStepsEnum.Billing];

    return steps.includes(currentStep);
  }

  onActivationChange(canDeactivate: boolean) {
    this.wizard.running = !canDeactivate;
  }

  onDismissSidebar() {
    this.sidebarToggle?.close();
    this.shoppingCartService.refresh();
  }

  onPaymentSuccess(order: Order) {
    this.wizard.order = order;
    this.nextStep();
  }

  /**
   * When the user is a training manager, we don't want to show the booking step.
   * Check if there are any cart item with booking during purchase allowed.
   */
  private isBookable(): boolean {
    if (this.currentUser.is('training_manager')) {
      return false;
    }

    return this.shoppingCart.cartItems.some(
      (item) =>
        item.quantity == 1 &&
        item.product?.isInstructorLed &&
        item.product?.bookDuringPurchase !== BookDuringPurchaseEnum.Disallowed
    );
  }
}
